import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FiMapPin, FiNavigation, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { MyContext } from '../../Context/MyContext';
import { getCurrentPosition } from '../../utils/geolocation';
import { loadActiveLocation, saveActiveLocation } from '../../utils/nearLocationStore';
import { resolveLocationDetails, resolveCatalogCityId } from '../../utils/reverseGeocode';
import useNearbyPropertyFetch from '../../hooks/useNearbyPropertyFetch';
import { resolveSearchRadiusKm } from '../../utils/nearLocationConfig';
import NearbyListingCard, { NearbyListingCardSkeleton } from './Components/home/NearbyListingCard';
import ContactModal from './Components/home/Poperty/ContactModal';
import { resolvePropertyContacts } from '../../utils/propertyContacts';
import StreetMapPickerModal from '../../Components/StreetMapPickerModal';
import './Components/home/Poperty/PropertyDetails.css';
import './NewListingsPage.css';
import './NearbyLocationsPage.css';

const SKELETON_COUNT = 4;

/** idle = no nearby results; nearby = radius results; city = district/city list */
const LIST_IDLE = 'idle';
const LIST_NEARBY = 'nearby';
const LIST_CITY = 'city';
const LIST_LOADING = 'loading';

const NearbyLocationsPage = () => {
  const {
    nearLocationConfig,
    refreshNearLocationConfig,
    selectedCity,
    cities,
    partners,
    admins,
  } = useContext(MyContext);

  const config = useMemo(() => nearLocationConfig || {}, [nearLocationConfig]);
  const configRef = useRef(config);
  configRef.current = config;

  const enabled = config.enabled !== false;
  const preferenceCityId = selectedCity?.id ? Number(selectedCity.id) : null;
  const pageLimit = config.results_per_page || 12;

  const { fetchWithFallback, fetchDistrict } = useNearbyPropertyFetch();

  const [listMode, setListMode] = useState(LIST_LOADING);
  const [phase, setPhase] = useState('loading');
  const [properties, setProperties] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeLocation, setActiveLocation] = useState(null);
  const [currentCity, setCurrentCity] = useState('');
  const [radiusUsed, setRadiusUsed] = useState(null);
  const [fallbackCityId, setFallbackCityId] = useState(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const [cityList, setCityList] = useState([]);
  const [cityPage, setCityPage] = useState(1);
  const [cityTotal, setCityTotal] = useState(0);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityLoadingMore, setCityLoadingMore] = useState(false);
  const [contactModal, setContactModal] = useState({ open: false, property: null });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const listener = (e) => setIsMobile(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const contactList = useMemo(() => {
    if (!contactModal.property) return [];
    return resolvePropertyContacts(contactModal.property, partners, admins);
  }, [contactModal.property, partners, admins]);

  const handleContactClick = useCallback((property) => {
    const list = resolvePropertyContacts(property, partners, admins);
    if (!list.length) {
      toast.info('No contact available for this property');
      return;
    }
    setContactModal({ open: true, property });
  }, [partners, admins]);

  useEffect(() => {
    refreshNearLocationConfig?.();
  }, [refreshNearLocationConfig]);

  const resolveCityId = useCallback((cityName, apiFallbackId, lat, lng) => {
    const fromCoords = resolveCatalogCityId({
      cityName,
      cityId: apiFallbackId,
      lat,
      lng,
      cities,
    });
    if (fromCoords) return fromCoords;
    return null;
  }, [cities]);

  const applyLocation = useCallback(async (loc) => {
    const details = await resolveLocationDetails(loc.lat, loc.lng, {
      name: loc.name,
      city: loc.city,
      cityId: loc.cityId,
    }, cities);
    const saved = saveActiveLocation({
      ...loc,
      name: details.name,
      city: details.city,
    });
    setActiveLocation(saved);
    setCurrentCity(details.city || '');
    setFallbackCityId(
      details.cityId
      || resolveCityId(details.city, null, loc.lat, loc.lng),
    );
    return saved;
  }, [cities, resolveCityId]);

  const runNearbySearch = useCallback(async (
    loc,
    pageNum = 1,
    append = false,
    prevList = [],
    resetCityView = false,
  ) => {
    const cfg = configRef.current;
    if (resetCityView) {
      setCityList([]);
      setListMode(LIST_LOADING);
      setPhase('loading');
    }

    const result = await fetchWithFallback({
      lat: loc.lat,
      lng: loc.lng,
      config: cfg,
      preferenceCityId,
      selectedCityName: loc.city || currentCity || selectedCity?.city_name,
      page: pageNum,
      append,
      previous: append ? prevList : [],
    });

    setRadiusUsed(result.radiusUsed);
    setPage(result.page || pageNum);
    setTotal(result.total || 0);

    if (result.mode === 'empty') {
      setProperties([]);
      setPhase(result.behavior === 'hide' ? 'hidden' : 'empty');
      if (resetCityView) setListMode(LIST_IDLE);
      return result;
    }

    setProperties(result.data);
    setPhase(result.data.length ? 'ready' : 'empty');
    setListMode(result.data.length ? LIST_NEARBY : LIST_IDLE);
    return result;
  }, [currentCity, fetchWithFallback, preferenceCityId, resolveCityId, selectedCity?.city_name]);

  const runCitySearch = useCallback(async (cityId, pageNum = 1, append = false, prevList = []) => {
    setListMode(LIST_CITY);
    const result = await fetchDistrict({
      cityId,
      page: pageNum,
      limit: pageLimit,
      append,
      previous: append ? prevList : [],
    });
    setCityList(result.data);
    setCityPage(result.page || pageNum);
    setCityTotal(result.total || 0);
    setPhase('empty');
    return result;
  }, [fetchDistrict, pageLimit]);

  const resolveInitialLocation = useCallback(async (useGps = false) => {
    const stored = !useGps ? loadActiveLocation() : null;
    if (stored) {
      return applyLocation(stored);
    }
    const pos = await getCurrentPosition();
    return applyLocation({ lat: pos.lat, lng: pos.lng, source: 'gps' });
  }, [applyLocation]);

  const bootstrap = useCallback(async (useGps = false) => {
    if (!enabled) {
      setPhase('disabled');
      setListMode(LIST_IDLE);
      return;
    }
    setError('');
    setListMode(LIST_LOADING);
    setPhase('loading');
    try {
      const loc = await resolveInitialLocation(useGps);
      await runNearbySearch(loc, 1, false, [], true);
    } catch (err) {
      setError(err.message || 'Could not access your location');
      const stored = loadActiveLocation();
      if (stored) {
        setActiveLocation(stored);
        const refreshed = await resolveLocationDetails(stored.lat, stored.lng, stored, cities);
        setCurrentCity(refreshed.city || '');
        setFallbackCityId(
          refreshed.cityId
          || resolveCityId(refreshed.city, null, stored.lat, stored.lng),
        );
        await runNearbySearch({ ...stored, city: refreshed.city }, 1, false, [], true);
      } else if (preferenceCityId) {
        setCurrentCity(selectedCity?.city_name || '');
        setFallbackCityId(preferenceCityId);
        setPhase('empty');
        setListMode(LIST_IDLE);
      } else {
        setPhase('error');
        setListMode(LIST_IDLE);
      }
    }
  }, [
    enabled,
    preferenceCityId,
    resolveCityId,
    resolveInitialLocation,
    runNearbySearch,
    selectedCity?.city_name,
  ]);

  // Initial load only — do not re-run when admin config polls/refreshes
  useEffect(() => {
    bootstrap(false);
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    setCityList([]);
    try {
      const loc = await resolveInitialLocation(true);
      await runNearbySearch(loc, 1, false, [], true);
    } catch (err) {
      setError(err.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleMapConfirm = async (picked) => {
    setCityList([]);
    const loc = await applyLocation(picked);
    await runNearbySearch(loc, 1, false, [], true);
  };

  const resolveDistrictCityId = useCallback(() => {
    if (!activeLocation) return fallbackCityId;
    return resolveCityId(
      currentCity || activeLocation.city,
      null,
      activeLocation.lat,
      activeLocation.lng,
    );
  }, [activeLocation, currentCity, fallbackCityId, resolveCityId]);

  const handleDistrictView = async () => {
    const cityId = resolveDistrictCityId();
    if (!cityId) return;
    setCityLoading(true);
    setError('');
    setListMode(LIST_CITY);
    try {
      await runCitySearch(cityId, 1, false);
    } catch (err) {
      setError(err.message || 'Could not load city properties');
      setListMode(LIST_IDLE);
    } finally {
      setCityLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!activeLocation) return;
    const next = page + 1;
    const result = await fetchWithFallback({
      lat: activeLocation.lat,
      lng: activeLocation.lng,
      config: configRef.current,
      preferenceCityId,
      selectedCityName: currentCity || selectedCity?.city_name,
      page: next,
      append: true,
      previous: properties,
    });
    setProperties(result.data);
    setPage(next);
    setTotal(result.total);
  };

  const handleCityLoadMore = async () => {
    const cityId = resolveDistrictCityId();
    if (!cityId || cityLoadingMore) return;
    setCityLoadingMore(true);
    try {
      await runCitySearch(cityId, cityPage + 1, true, cityList);
    } finally {
      setCityLoadingMore(false);
    }
  };

  const hasMore = properties.length < total;
  const cityHasMore = cityList.length < cityTotal;
  const cityLabel = currentCity || 'Your City';
  const searchRadius = radiusUsed ?? resolveSearchRadiusKm(config);
  const showDistrictBtn = (listMode === LIST_IDLE || listMode === LIST_CITY)
    && config.fallback_behavior === 'district'
    && (fallbackCityId || currentCity);
  const isLoading = listMode === LIST_LOADING && !cityLoading;

  return (
    <div className="new-listings-page nearby-locations-page">
      <header className="nlp-hero">
        <div className="nlp-hero__glow" aria-hidden />
        <div className="nlp-hero__inner">
          <span className="nlp-hero__badge">
            <FiNavigation /> Near You
          </span>
          <h1>{config.page_title || 'Nearby Locations'}</h1>
          <p>{config.page_subtitle}</p>

          {(currentCity || isLoading) && (
            <div className="nlp-location-name">
              <FiMapPin size={16} aria-hidden />
              <span>{isLoading ? 'Detecting your city…' : currentCity}</span>
            </div>
          )}

          {enabled && !isLoading && (
            <div className="nlp-hero-actions">
              <button
                type="button"
                className={`nlp-hero-action-btn${refreshing ? ' nlp-hero-action-btn--spin' : ''}`}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <FiRefreshCw size={15} />
                Refresh Location
              </button>
              {config.allow_manual_location !== false && (
                <button
                  type="button"
                  className="nlp-hero-action-btn nlp-hero-action-btn--outline"
                  onClick={() => setMapOpen(true)}
                >
                  <FiMapPin size={15} />
                  Change Location
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="nlp-main nlp-main--wide">
        {isLoading && (
          <div className="nlp-results-section">
            <p className="nlp-status nlp-status--inline">Loading properties near your location…</p>
            <div className="nlp-grid">
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <NearbyListingCardSkeleton key={i} />
              ))}
            </div>
          </div>
        )}

        {phase === 'disabled' && (
          <div className="nlp-status nlp-status--muted">
            Nearby location search is currently disabled.
          </div>
        )}

        {phase === 'error' && listMode !== LIST_CITY && (
          <div className="nlp-status nlp-status--error">
            {error}
            <p>Enable location access or pick a location on the map.</p>
            {config.allow_manual_location && (
              <button type="button" className="nlp-district-btn" onClick={() => setMapOpen(true)}>
                Choose on Map
              </button>
            )}
          </div>
        )}

        {phase === 'hidden' && listMode !== LIST_CITY && (
          <div className="nlp-status nlp-status--muted">{config.empty_message}</div>
        )}

        {listMode === LIST_IDLE && (
          <div className="nlp-fallback">
            <FiMapPin size={28} />
            <h2>{config.empty_message}</h2>
            <p>
              {error || `No listings within ${searchRadius} km of ${cityLabel}.`}
            </p>
            {showDistrictBtn && (
              <button
                type="button"
                className="nlp-district-btn"
                onClick={handleDistrictView}
                disabled={cityLoading}
              >
                {cityLoading ? 'Loading…' : `View Properties in ${cityLabel}`}
              </button>
            )}
          </div>
        )}

        {listMode === LIST_NEARBY && properties.length > 0 && (
          <div className="nlp-results-section">
            <div className="nlp-toolbar">
              <span>
                {`${total} propert${total === 1 ? 'y' : 'ies'} within ${searchRadius} km of ${cityLabel}`}
              </span>
            </div>
            <div className="nlp-grid">
              {properties.map((p) => (
                <NearbyListingCard
                  key={p.id}
                  property={p}
                  showDistance
                  onContactClick={handleContactClick}
                />
              ))}
            </div>
            {hasMore && (
              <div className="nlp-more">
                <button type="button" className="nlp-more-btn" onClick={handleLoadMore}>
                  Load more
                </button>
              </div>
            )}
          </div>
        )}

        {listMode === LIST_CITY && (
          <div className="nlp-city-section">
            <div className="nlp-fallback nlp-fallback--compact">
              <FiMapPin size={22} />
              <h2>{config.empty_message}</h2>
              <p>{`No listings within ${searchRadius} km of ${cityLabel}.`}</p>
              {showDistrictBtn && (
                <button
                  type="button"
                  className="nlp-district-btn nlp-district-btn--active"
                  disabled
                >
                  View Properties in {cityLabel}
                </button>
              )}
            </div>

            <div className="nlp-city-results">
              <div className="nlp-toolbar">
                <h2 className="nlp-city-title">Properties in {cityLabel}</h2>
                {!cityLoading && cityTotal > 0 && (
                  <span className="nlp-city-count">
                    {cityTotal} listing{cityTotal === 1 ? '' : 's'}
                  </span>
                )}
              </div>

              {cityLoading && (
                <div className="nlp-grid">
                  {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                    <NearbyListingCardSkeleton key={i} />
                  ))}
                </div>
              )}

              {!cityLoading && cityList.length > 0 && (
                <>
                  <div className="nlp-grid">
                    {cityList.map((p) => (
                      <NearbyListingCard
                        key={p.id}
                        property={p}
                        showDistance={false}
                        onContactClick={handleContactClick}
                      />
                    ))}
                  </div>
                  {cityHasMore && (
                    <div className="nlp-more">
                      <button
                        type="button"
                        className="nlp-more-btn"
                        onClick={handleCityLoadMore}
                        disabled={cityLoadingMore}
                      >
                        {cityLoadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}

              {!cityLoading && cityList.length === 0 && (
                <div className="nlp-status nlp-status--muted">
                  No properties found in {cityLabel}.
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <StreetMapPickerModal
        open={mapOpen && config.allow_manual_location !== false}
        onClose={() => setMapOpen(false)}
        onConfirm={handleMapConfirm}
        initialLat={activeLocation?.lat}
        initialLng={activeLocation?.lng}
        title="Change Location"
      />

      <ContactModal
        open={contactModal.open}
        onClose={() => setContactModal({ open: false, property: null })}
        contacts={contactList}
        title="Call / WhatsApp"
        property={contactModal.property}
        isMobile={isMobile}
      />
    </div>
  );
};

export default NearbyLocationsPage;
